import {
  priceRoomNight,
  type HotelInventoryListItem,
  type HotelInventoryMealOption,
  type HotelRateInput,
} from '@utazzo/shared';

/** One configured room's occupancy (from the Configure step). */
export interface PricingConfigRoom {
  adults: number;
  children: number;
  childrenNoBed: number;
  infants: number;
}

/**
 * Occupancy-aware stay price for a preferred-hotel rate card, in the rate's
 * currency. Shared by the hotel picker AND the "Change Rooms & Meals" editor so
 * both quote a room the same way: base occupancy + per-adult + extra-adult (+
 * the selected meal's per-pax supplement), summed across EVERY configured room
 * and multiplied by the stay nights. Occupancy always comes from Configure - it
 * is never re-entered in the popup.
 *
 * Returns `unavailable` (skip-and-flag) when any configured room can't fit the
 * rate card (occupancy engine says so), matching the picker's behaviour.
 */
export function priceInventoryForConfig(
  inv: HotelInventoryListItem,
  maxOccupancy: number,
  meal: HotelInventoryMealOption | null,
  configRooms: PricingConfigRoom[],
  nights: number,
): { total: number; perRoom: number[]; unavailable: string | null } {
  const rate: HotelRateInput = {
    fixedRoomCost: inv.fixedRoomCost,
    adultPrice: inv.adultPrice,
    childWithBedPrice: inv.childWithBedPrice,
    childWithoutBedPrice: inv.childWithoutBedPrice,
    infantPrice: inv.infantPrice,
    extraAdultPrice: inv.extraAdultPrice,
    // Meal is a separate pick: its per-pax supplement rides on top of the room
    // rate. No meal selected => Room Only => 0 supplement.
    mealPerAdult: meal?.perAdult ?? 0,
    mealPerChild: meal?.perChild ?? 0,
    baseAdults: inv.baseOccupancy ?? inv.occupancyPaxCount ?? 2,
    baseChildrenIncluded: 1,
    maxOccupancy,
  };
  const n = Math.max(1, nights);
  let total = 0;
  // Per-room stay price (same order as configRooms), feeds roomCostWeights.
  const perRoom: number[] = [];
  for (const r of configRooms) {
    const p = priceRoomNight(rate, r);
    if (p.unavailableReason) return { total: 0, perRoom: [], unavailable: p.unavailableReason };
    total += p.amount;
    perRoom.push(p.amount * n);
  }
  return { total: total * n, perRoom, unavailable: null };
}
